import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import { LayoutNorthArrow, NORTH_ARROW_STYLES } from './LayoutNorthArrow';
import { DEFAULT_NORTH_ARROW_SYMBOL } from './layoutDocument';
import { northArrowCalculatedAngle } from './northArrowOrientation';
import { PointSymbolPanel, type PointSymbolDraft } from './PointSymbolPanel';
import type {
  MapFrameElement,
  NorthArrowElement,
  NorthArrowOrientation,
  NorthArrowStyleId,
} from './layoutTypes';

export interface NorthArrowPropertiesPanelProps {
  readonly arrow: NorthArrowElement;
  readonly frames: readonly MapFrameElement[];
  /** Symbol size in points, derived from the element box. */
  readonly sizePt: number;
  readonly onChange: (patch: Partial<NorthArrowElement>) => void;
  readonly onSymbolApply: (draft: PointSymbolDraft) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

const SCALE_OPTIONS = [
  500,
  1_000,
  2_500,
  5_000,
  10_000,
  25_000,
  50_000,
  100_000,
  250_000,
  500_000,
  1_000_000,
  2_500_000,
] as const;

function numberOrNull(value: string): number | null {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function NorthArrowPropertiesPanel({
  arrow,
  frames,
  sizePt,
  onChange,
  onSymbolApply,
  onDelete,
  onClose,
}: NorthArrowPropertiesPanelProps) {
  const [view, setView] = useState<'element' | 'symbol'>('element');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const frame = frames.find((candidate) => candidate.id === arrow.mapFrameId)
    ?? frames[0]
    ?? null;
  const calculated = frame
    ? northArrowCalculatedAngle(arrow, frame.camera)
    : -arrow.calibrationAngle;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  const field = {
    minHeight: 32,
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: `0 ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  };
  const row = {
    display: 'grid',
    gridTemplateColumns: `${tokens.density.touchMin * 2.75}px minmax(0, 1fr)`,
    alignItems: 'center',
    gap: tokens.space[2],
  };
  const iconButton: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: tokens.density.rowDense,
    minHeight: tokens.density.rowDense,
    border: 0,
    background: 'transparent',
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
  };
  const menuItem: CSSProperties = {
    display: 'block',
    width: '100%',
    minHeight: tokens.density.rowDense,
    padding: `0 ${tokens.space[2]}px`,
    border: 0,
    borderRadius: tokens.radius.sm,
    background: 'transparent',
    color: tokens.color.ink.primary,
    cursor: 'pointer',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    textAlign: 'left',
  };
  // Keep live layouts created before the marker-layer model was added usable
  // across Vite HMR; persisted layout loading can use the same migration seam.
  const symbol = {
    ...DEFAULT_NORTH_ARROW_SYMBOL,
    ...arrow.symbol,
    marker: {
      ...DEFAULT_NORTH_ARROW_SYMBOL.marker,
      ...arrow.symbol?.marker,
    },
    halo: {
      ...DEFAULT_NORTH_ARROW_SYMBOL.halo,
      ...arrow.symbol?.halo,
    },
  };

  return (
    <aside
      data-testid="north-arrow-properties-panel"
      aria-label="North arrow properties"
      style={{
        position: 'absolute',
        zIndex: tokens.zIndex.dock,
        top: tokens.space[3],
        right: tokens.space[3],
        width: 300,
        maxHeight: `calc(100% - ${tokens.space[6]}px)`,
        overflowY: 'auto',
        padding: tokens.space[3],
        border: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
        borderRadius: tokens.radius.lg,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: tokens.space[2],
          marginBottom: tokens.space[3],
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          {view === 'symbol' ? (
            <button
              type="button"
              aria-label="Back to element properties"
              onClick={() => setView('element')}
              style={iconButton}
            >
              <Icon name="arrow_back" size={20} decorative />
            </button>
          ) : null}
          <div>
            <div style={{ color: tokens.color.semantic.info, fontSize: tokens.font.size.lg }}>
              Element
            </div>
            <strong style={{ fontSize: tokens.font.size.md }}>North Arrow</strong>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[1] }}>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="North arrow element menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((previous) => !previous)}
              style={iconButton}
            >
              <Icon name="menu" size={20} decorative />
            </button>
            {menuOpen ? (
              <div
                role="menu"
                aria-label="North arrow element actions"
                data-testid="north-arrow-element-menu"
                style={{
                  position: 'absolute',
                  zIndex: tokens.zIndex.tooltip,
                  top: '100%',
                  right: 0,
                  width: tokens.layout.popoverXs,
                  padding: tokens.space[1],
                  background: tokens.color.surface[2],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  boxShadow: tokens.elevation[2],
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setView('symbol');
                    setMenuOpen(false);
                  }}
                  style={menuItem}
                >
                  Format point symbol
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onChange({ visible: !arrow.visible });
                    setMenuOpen(false);
                  }}
                  style={menuItem}
                >
                  {arrow.visible ? 'Hide element' : 'Show element'}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={arrow.locked}
                  title={arrow.locked ? 'Unlock the element first' : undefined}
                  onClick={() => {
                    onDelete();
                    setMenuOpen(false);
                  }}
                  style={{
                    ...menuItem,
                    color: arrow.locked
                      ? tokens.color.ink.muted
                      : tokens.color.semantic.danger,
                    cursor: arrow.locked ? 'not-allowed' : 'pointer',
                  }}
                >
                  Delete element
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close north arrow properties"
            onClick={onClose}
            style={{
              ...iconButton,
              fontSize: tokens.font.size.xl,
            }}
          >
            ×
          </button>
        </div>
      </div>

      {view === 'symbol' ? (
        <PointSymbolPanel
          styleId={arrow.styleId}
          symbol={symbol}
          sizePt={sizePt}
          baseRotationDeg={calculated}
          onApply={onSymbolApply}
        />
      ) : (
        <>
      <section style={{ display: 'grid', gap: tokens.space[3] }}>
        <h3
          style={{
            margin: 0,
            paddingBottom: tokens.space[1],
            borderBottom: `${tokens.focus.ringWidth}px solid ${tokens.color.semantic.info}`,
            fontSize: tokens.font.size.sm,
          }}
        >
          General
        </h3>
        <label style={row}>
          <span>Name</span>
          <input
            aria-label="North arrow name"
            value={arrow.name}
            onChange={(event) => onChange({ name: event.target.value })}
            style={field}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          <input
            type="checkbox"
            checked={arrow.visible}
            onChange={(event) => onChange({ visible: event.target.checked })}
          />
          Visible
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          <input
            type="checkbox"
            checked={arrow.locked}
            onChange={(event) => onChange({ locked: event.target.checked })}
          />
          Locked
        </label>
      </section>

      <section style={{ display: 'grid', gap: tokens.space[3], marginTop: tokens.space[5] }}>
        <h3 style={{ margin: 0, fontSize: tokens.font.size.sm }}>Visibility range</h3>
        <label style={row}>
          <span>Maximum scale</span>
          <select
            aria-label="North arrow maximum scale"
            value={arrow.maximumScaleDenominator ?? ''}
            onChange={(event) =>
              onChange({ maximumScaleDenominator: numberOrNull(event.target.value) })
            }
            style={field}
          >
            <option value="">None</option>
            {SCALE_OPTIONS.map((scale) => (
              <option key={scale} value={scale}>1:{scale.toLocaleString()}</option>
            ))}
          </select>
        </label>
        <label style={row}>
          <span>Minimum scale</span>
          <select
            aria-label="North arrow minimum scale"
            value={arrow.minimumScaleDenominator ?? ''}
            onChange={(event) =>
              onChange({ minimumScaleDenominator: numberOrNull(event.target.value) })
            }
            style={field}
          >
            <option value="">None</option>
            {SCALE_OPTIONS.map((scale) => (
              <option key={scale} value={scale}>1:{scale.toLocaleString()}</option>
            ))}
          </select>
        </label>
      </section>

      <section style={{ display: 'grid', gap: tokens.space[3], marginTop: tokens.space[5] }}>
        <h3 style={{ margin: 0, fontSize: tokens.font.size.sm }}>North Arrow</h3>
        <label style={row}>
          <span>Map Frame</span>
          <select
            aria-label="North arrow map frame"
            value={arrow.mapFrameId}
            onChange={(event) => onChange({ mapFrameId: event.target.value })}
            style={field}
          >
            {frames.map((candidate, index) => (
              <option key={candidate.id} value={candidate.id}>
                {frames.length === 1 ? 'Map Frame' : `Map Frame ${index + 1}`}
              </option>
            ))}
          </select>
        </label>
        <label style={row}>
          <span>Type</span>
          <select
            aria-label="North arrow orientation type"
            value={arrow.orientation}
            onChange={(event) =>
              onChange({ orientation: event.target.value as NorthArrowOrientation })
            }
            style={field}
          >
            <option value="map">Map North</option>
            <option value="true">True North</option>
            <option value="magnetic">Magnetic North (estimate)</option>
            <option value="grid">Grid North</option>
          </select>
        </label>
        <label style={row}>
          <span>Calibration angle</span>
          <input
            aria-label="North arrow calibration angle"
            type="number"
            min="-360"
            max="360"
            step="0.1"
            value={arrow.calibrationAngle}
            onChange={(event) => {
              const value = Number(event.target.value);
              onChange({
                calibrationAngle: Number.isFinite(value)
                  ? Math.max(-360, Math.min(360, value))
                  : 0,
              });
            }}
            style={field}
          />
        </label>
        <div style={row}>
          <span>Calculated angle</span>
          <output aria-label="North arrow calculated angle">
            {calculated.toFixed(4)}°
          </output>
        </div>
        {arrow.orientation === 'magnetic' ? (
          <p style={{ margin: 0, color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
            Magnetic angle is an approximate 2025 magnetic-pole bearing, not a
            certified World Magnetic Model value.
          </p>
        ) : null}
        <label style={row}>
          <span>Symbol</span>
          <select
            aria-label="North arrow symbol"
            value={arrow.styleId}
            onChange={(event) =>
              onChange({ styleId: event.target.value as NorthArrowStyleId })
            }
            style={field}
          >
            {NORTH_ARROW_STYLES.map((style) => (
              <option key={style.id} value={style.id}>{style.label}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          aria-label="Edit point symbol"
          title="Open the Point Symbol formatting pane"
          onClick={() => setView('symbol')}
          style={{
            justifySelf: 'center',
            width: 82,
            height: 102,
            padding: tokens.space[2],
            background: tokens.color.canvas.light,
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            cursor: 'pointer',
          }}
        >
          <LayoutNorthArrow
            styleId={arrow.styleId}
            bearingDeg={0}
            rotationDeg={calculated + symbol.angle}
            color={symbol.color}
            marker={symbol.marker}
            halo={symbol.halo}
          />
        </button>
        <p style={{ margin: 0, color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
          Click the symbol to format colour, size, angle, and halo.
        </p>
      </section>
        </>
      )}
    </aside>
  );
}
